import React, { createContext, useCallback, useEffect, useState } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Favoritos e fixados são POR USUÁRIO e aparecem em dois lugares ao mesmo
 * tempo: na linha da conversa e no cabeçalho do atendimento aberto.
 *
 * Antes cada tela guardava seu próprio estado local e lia `ticket.favoritoDoUsuario`,
 * que só é recalculado quando a lista é buscada de novo no servidor -- por isso
 * a estrela só aparecia depois do F5 e o cabeçalho nunca ficava marcado.
 *
 * Aqui a verdade fica em um lugar só. Quem favorita avisa o contexto, e todas
 * as telas que usam o hook re-renderizam na hora.
 */
const FavoritosContext = createContext({
  favoritos: new Set(),
  fixados: new Set(),
  ehFavorito: () => false,
  ehFixado: () => false,
  alternarFavorito: async () => {},
  alternarFixado: async () => {},
});

const FavoritosProvider = ({ children }) => {
  const [favoritos, setFavoritos] = useState(new Set());
  const [fixados, setFixados] = useState(new Set());

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/contact-favorites");
      setFavoritos(new Set((data.favoritos || []).map(Number)));
      setFixados(new Set((data.fixados || []).map(Number)));
    } catch (err) {
      // sem favoritos carregados o sistema continua funcionando;
      // não vale incomodar o usuário com um toast por isso
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const ehFavorito = useCallback(
    (contactId) => favoritos.has(Number(contactId)),
    [favoritos]
  );

  const ehFixado = useCallback(
    (contactId) => fixados.has(Number(contactId)),
    [fixados]
  );

  const aplicar = (contactId, resposta) => {
    const id = Number(contactId);

    setFavoritos((antes) => {
      const novo = new Set(antes);
      if (resposta.favorito) novo.add(id);
      else novo.delete(id);
      return novo;
    });

    setFixados((antes) => {
      const novo = new Set(antes);
      if (resposta.fixado) novo.add(id);
      else novo.delete(id);
      return novo;
    });
  };

  const alternarFavorito = useCallback(async (contactId) => {
    if (!contactId) return;
    try {
      const { data } = await api.post(`/contact-favorites/${contactId}/toggle`);
      aplicar(contactId, data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const alternarFixado = useCallback(async (contactId) => {
    if (!contactId) return;
    try {
      const { data } = await api.post(`/contact-favorites/${contactId}/pin`);
      aplicar(contactId, data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  return (
    <FavoritosContext.Provider
      value={{
        favoritos,
        fixados,
        ehFavorito,
        ehFixado,
        alternarFavorito,
        alternarFixado,
        recarregarFavoritos: carregar,
      }}
    >
      {children}
    </FavoritosContext.Provider>
  );
};

export { FavoritosContext, FavoritosProvider };
